import { useState } from 'react';
import {
    Upload,
    FileText,
    Briefcase,
    TrendingUp,
    AlertCircle,
    Star,
    CheckCircle,
    Flag,
    Info,
    BarChart,
    ArrowRight,
    FileUp,
    Plus
} from 'lucide-react';
const Main = () => {
    const [selectedFile, setSelectedFile] = useState(null);

    // Mock case studies data
    const caseStudies = [
        {
            title: "AI Patent Portfolio Strategy",
            client: "Tech Enterprise Ltd",
            outcome: "89% success rate",
            value: "£2.5M",
            industry: "Technology",
            expertise: ["AI/ML", "Patent Strategy"],
            impact: [
                "Protected core IP assets",
                "Enabled international expansion",
                "Secured Series C funding"
            ]
        }
    ];

    // Mock capabilities data
    const capabilities = {
        core: [
            {
                area: "IP Strategy",
                expertise: 92,
                projects: 234,
                specializations: ["AI/ML", "Biotech", "Software"]
            },
            {
                area: "Portfolio Management",
                expertise: 88,
                projects: 156,
                specializations: ["Trademarks", "Patents", "Copyright"]
            }
        ],
        recent: [
            {
                skill: "AI Patent Filing",
                completedProjects: 45,
                successRate: "92%",
                growth: "+28% YoY"
            }
        ]
    };

    // Mock missed opportunities feedback
    const missedOpportunities = {
        recent: [
            {
                rfp: "Global Tech IP Strategy",
                reason: "Missing AI specialization proof",
                improvement: "Add relevant case studies",
                potentialValue: "£150K"
            },
            {
                rfp: "Biotech Patent Portfolio",
                reason: "Limited team availability",
                improvement: "Expand specialized team",
                potentialValue: "£200K"
            }
        ],
        trends: [
            {
                area: "Team Capacity",
                impact: "High",
                frequency: 8,
                suggestion: "Consider strategic hiring"
            },
            {
                area: "Case Studies",
                impact: "Medium",
                frequency: 5,
                suggestion: "Document recent successes"
            }
        ]
    };

    return (
        <div className="space-y-6">
            {/* Case Studies Upload */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        <span className='font-bold'> Case Studies & Success Stories </span>
                    </div>
                    <div>
                        Showcase your expertise and track record
                    </div>
                </div>
                <div>
                    {/* Upload Section */}
                    <div className="border-2 border-dashed rounded-lg p-6 mb-6">
                        <div className="flex flex-col items-center text-center">
                            <Upload className="w-8 h-8 mb-2 text-gray-400" />
                            <h3 className="font-medium mb-1">Upload Case Study</h3>
                            <p className="text-sm text-gray-500 mb-4">
                                PDF, DOC up to 10MB
                            </p>
                            <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                Select File</button>
                        </div>
                    </div>

                    {/* Existing Case Studies */}
                    <div className="space-y-4">
                        {caseStudies.map((study, index) => (
                            <div key={index} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-medium">{study.title}</h3>
                                        <p className="text-sm text-gray-500">{study.client}</p>
                                    </div>
                                    <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                        {study.outcome}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <div className="text-sm text-gray-500">Value</div>
                                        <div className="font-medium">{study.value}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500">Industry</div>
                                        <div className="font-medium">{study.industry}</div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    {study.impact.map((impact, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                            <CheckCircle className="w-4 h-4 text-[#22c55e]" />
                                            {impact}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Capabilities Matrix */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5" />
                        <span className='font-bold'> Capabilities Profile</span>
                    </div>
                    <div>
                        Comprehensive overview of your services and expertise
                    </div>
                </div>
                <div>
                    <div className="space-y-6">
                        {/* Core Capabilities */}
                        <div>
                            <h3 className="font-medium mb-4">Core Practice Areas</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {capabilities.core.map((cap, index) => (
                                    <div key={index} className="border rounded-lg p-4">
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <h4 className="font-medium">{cap.area}</h4>
                                                <p className="text-sm text-gray-500">{cap.projects} projects</p>
                                            </div>
                                            <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800">
                                                {cap.expertise}% Expertise
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {cap.specializations.map((spec, idx) => (
                                                <div key={idx} className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                                    {spec}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recent Capabilities */}
                        <div>
                            <h3 className="font-medium mb-4">Growing Expertise Areas</h3>
                            <div className="space-y-4">
                                {capabilities.recent.map((skill, index) => (
                                    <div key={index} className="border rounded-lg p-4">
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <h4 className="font-medium">{skill.skill}</h4>
                                                <p className="text-sm text-gray-500">
                                                    {skill.completedProjects} completed projects
                                                </p>
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                                    {skill.successRate}
                                                </div>
                                                <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800">
                                                    {skill.growth}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Opportunity Analysis */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        <span className='font-bold'> Opportunity Analysis </span>
                    </div>
                    <div>
                        Insights from missed opportunities and market trends
                    </div>
                </div>
                <div>
                    {/* Recent Missed Opportunities */}
                    <div className="mb-6">
                        <h3 className="font-medium mb-4">Recent Missed Opportunities</h3>
                        <div className="space-y-4">
                            {missedOpportunities.recent.map((opp, index) => (
                                <div key={index} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h4 className="font-medium">{opp.rfp}</h4>
                                            <p className="text-sm text-gray-500">{opp.reason}</p>
                                        </div>
                                        <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>{opp.potentialValue}</div>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-[#16a34a]">
                                        <ArrowRight className="w-4 h-4" />
                                        {opp.improvement}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Improvement Areas */}
                    <div>
                        <h3 className="font-medium mb-4">Trend Analysis</h3>
                        <div className="space-y-4">
                            {missedOpportunities.trends.map((trend, index) => (
                                <div key={index} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h4 className="font-medium">{trend.area}</h4>
                                            <p className="text-sm text-gray-500">
                                                Impact: {trend.impact} • Frequency: {trend.frequency} times
                                            </p>
                                        </div>
                                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">                                            View Details
                                        </button>
                                    </div>
                                    <div className="text-sm">
                                        Suggestion: {trend.suggestion}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Main